import React, { useEffect, useRef, useState } from 'react';
import { openObsChannel } from './remoteTransport';
import { MSG, STALE_AFTER_MS, HEARTBEAT_MS, normalizeCode, isValidCode } from './remoteProtocol';
import { DEFAULT_STYLE } from '../display/displayChannel';
import { slideText } from '../lib/slides';
import { textEffectCss } from '../display/themes';

const FADE_MS = 250;
const codeFromUrl = () => normalizeCode(new URLSearchParams(window.location.search).get('c') || '');

/**
 * Livestream output (/obs): add it to OBS as a Browser Source. The page is
 * transparent and draws the on-air verse or lyric line as a lower third, so
 * it sits over the camera. It shows nothing when the console is off air or
 * has gone quiet (so a stale verse can never stay on the stream).
 * All text is rendered as React text, never innerHTML.
 */
const ObsScreen = () => {
  const code = isValidCode(codeFromUrl()) ? codeFromUrl() : '';
  const [output, setOutput] = useState(null);
  const [seen, setSeen] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const remoteRef = useRef(null);

  // OBS composites the page over the video, so nothing may paint a background.
  useEffect(() => {
    document.title = 'SleekHear OBS';
    const prev = [document.documentElement.style.background, document.body.style.background];
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    return () => {
      document.documentElement.style.background = prev[0];
      document.body.style.background = prev[1];
    };
  }, []);

  useEffect(() => {
    if (!code) return undefined;
    const remote = openObsChannel(code, {
      onStatus: (s) => { if (s === 'connected' && remoteRef.current) remoteRef.current.send({ t: MSG.HELLO }); },
      onMessage: (msg) => {
        if (msg.t === MSG.OUTPUT && msg.output) {
          setOutput(msg.output);
          setSeen(Date.now());
          setNow(Date.now());
        }
      },
    });
    remoteRef.current = remote;
    if (!remote) return undefined;
    const timer = setInterval(() => setNow(Date.now()), HEARTBEAT_MS / 2);
    return () => { clearInterval(timer); remote.close(); remoteRef.current = null; };
  }, [code]);

  if (!code) {
    return (
      <div style={{ color: '#fff', font: '16px sans-serif', padding: 24, textShadow: '0 1px 3px #000' }}>
        SleekHear OBS: this link needs a pairing code (…/obs?c=XXXXXXXX). Copy it from the Remote panel in SleekHear.
      </div>
    );
  }

  const fresh = output && now - seen < STALE_AFTER_MS;
  const content = output && output.content;
  const s = { ...DEFAULT_STYLE, ...((output && output.style) || {}) };

  let label = '';
  let text = '';
  let key = 'blank';
  if (fresh && output.isLive && content) {
    if (content.type === 'scripture') {
      label = `${content.reference || ''}${content.version ? ` (${content.version})` : ''}`;
      text = slideText(content, output.slides, output.currentLine);
      key = `s:${label}:${text}`;
    } else if (content.type === 'song') {
      label = content.title || '';
      text = slideText(content, output.slides, output.currentLine);
      key = `g:${label}:${output.currentLine}`;
    }
  }

  return (
    <div data-testid="obs-root" style={{ position: 'fixed', inset: 0, background: 'transparent', overflow: 'hidden', cursor: 'none' }}>
      <style>{'@keyframes sleekFade{from{opacity:0}to{opacity:1}}'}</style>
      {text && (
        <div
          key={key}
          style={{
            position: 'absolute', left: '6%', right: '6%', bottom: '6%',
            padding: '24px 40px', borderRadius: 16, background: 'rgba(0,0,0,0.62)',
            color: s.fontColor, fontFamily: s.fontFamily, textAlign: 'center',
            animation: `sleekFade ${FADE_MS}ms ease-out`, ...textEffectCss(s.textEffect),
          }}
        >
          {label && (
            <div style={{ fontSize: s.fontSize * 0.45, fontWeight: 'bold', opacity: 0.85, marginBottom: 8 }}>{label}</div>
          )}
          <div style={{ fontSize: s.fontSize * 0.7, lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{text}</div>
        </div>
      )}
    </div>
  );
};

export default ObsScreen;
